import SvgBuilding from '@blackhyve/common/src/icons/Building';
import GanttIcon from '@blackhyve/common/src/icons/GanttIcon';
import { Person } from '@mui/icons-material';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import List from '@mui/icons-material/List';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TimelineIcon from '@mui/icons-material/Timeline';
import { EditContext } from 'components/common/v3/DisplayWithEditAccess';
import { ContentLayout } from 'components/layouts/ContentLayout';
import { SecondaryNav, SecondaryNavLink } from 'components/navigation/SecondaryNav';
import { SHOW_SNACKBAR } from 'constants/ActionTypes';
import Jobwalk from 'features/jobwalk/routes/Jobwalk';
import { Locations } from 'features/locations';
import { PlanningBoard } from 'features/planningBoard';
import { ProjectUsers } from 'features/projectCompanies';
import { fetchProject, selectProjectById } from 'features/projects/store/projectsSlice';
import { setRecipeDrawer } from 'features/recipes/store/recipesSlice';
import StuckPointDetails from 'features/stuckPoints/components/StuckPointDetails';
import StuckPoints from 'features/stuckPoints/routes/StuckPoints';
import { useGetAllProjectSuckPointsQuery } from 'features/stuckPoints/store/stuckPoint.api';
import { useCanEditProject } from 'hooks/useCanEditProject';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { clearDependencies } from '../../dependencies/store/dependenciesSlice';
import { clearLocations } from '../../locations/store/locationsSlice';
import { clearZones } from '../../locations/store/zonesSlice';
import { ProjectsGantt } from '../../projectsGantt/routes/ProjectsGantt';
import { clearTasks } from '../../tasks/store/tasksSlice';
import ProjectActivityFeed from '../components/ProjectActivityFeed';
import ProjectNotificationLog from '../components/ProjectNotificationLog';
import { Projects } from './Projects';
import { stuckPointsRoutes } from 'features/stuckPoints';

const ProjectsLayout = () => {
  return (
    <ContentLayout
      header={
        <SecondaryNav title={'Projects'}>
          <SecondaryNavLink startIcon={<List />} to={''}>
            List
          </SecondaryNavLink>
          <SecondaryNavLink startIcon={<GanttIcon />} to={'gantt'}>
            Gantt
          </SecondaryNavLink>
        </SecondaryNav>
      }
    >
      <Outlet />
    </ContentLayout>
  );
};

const ProjectLayout = () => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const projectName = useSelector((state) => selectProjectById(state, projectId)?.name);
  const canEdit = useCanEditProject(projectId);
  const { data: openStuckPoint } = useGetAllProjectSuckPointsQuery({ projectId });

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProject(projectId))
        .unwrap()
        .catch((error) => {
          dispatch({
            type: SHOW_SNACKBAR,
            message: 'Project Not Found',
            variant: 'error',
          });
          console.error(error);
          navigate('/', { replace: true });
        });
    }
    return () => {
      dispatch(clearTasks());
      dispatch(clearDependencies());
      dispatch(clearLocations());
      dispatch(clearZones());
      dispatch(setRecipeDrawer({ open: false }));
    };
  }, [dispatch, navigate, projectId]);

  return (
    <ContentLayout
      header={
        <SecondaryNav title={projectName}>
          <SecondaryNavLink startIcon={<GanttIcon fontSize={'small'} />} to={'planning-board'}>
            Planning
          </SecondaryNavLink>
          <SecondaryNavLink startIcon={<SvgBuilding />} to={'locations'}>
            Locations
          </SecondaryNavLink>
          <SecondaryNavLink startIcon={<Person />} to={'users'}>
            Users
          </SecondaryNavLink>
          <SecondaryNavLink startIcon={<DirectionsWalkIcon />} to={'jobwalks'}>
            Jobwalk
          </SecondaryNavLink>
          <SecondaryNavLink startIcon={<PendingActionsIcon />} to={'stuck-points'}>
            Stuck Points {openStuckPoint > 0 ? `(${openStuckPoint})` : '(0)'}
          </SecondaryNavLink>
          <SecondaryNavLink startIcon={<TimelineIcon />} to={'activity-feeds'}>
            Activities
          </SecondaryNavLink>
        </SecondaryNav>
      }
    >
      <EditContext.Provider value={canEdit}>
        <Outlet />
      </EditContext.Provider>
    </ContentLayout>
  );
};

export const projectsRoutes = [
  {
    path: '',
    element: (
      // <RequireRole
      //   exclude
      //   fallbackComponent={<Navigate replace to={'..'} />}
      //   roles={['contractor_contact']}
      // >
      <ProjectsLayout />
      // </RequireRole>
    ),
    children: [
      { index: true, element: <Projects /> },
      { path: 'gantt', element: <ProjectsGantt /> },
    ],
  },
  {
    path: ':projectId',
    element: <ProjectLayout />,
    children: [
      { path: 'planning-board', element: <PlanningBoard /> },
      // { path: 'production-board', element: <ProductionBoard /> },
      // { path: 'dashboard', element: <Dashboard /> },
      // { path: 'flow-board', element: <FlowBoard /> },
      { path: 'locations', element: <Locations /> },
      { path: 'activity-feeds', element: <ProjectActivityFeed /> },
      { path: 'notification-log', element: <ProjectNotificationLog /> },
      { path: 'users', element: <ProjectUsers /> },
      { path: 'jobwalks', element: <Jobwalk /> },
      { path: 'stuck-points', children: stuckPointsRoutes },
      { index: true, element: <Navigate replace to="planning-board" /> },
    ],
  },
  // { path: ':projectId/tasks', children: tasksRoutes },
  { path: '*', element: <Navigate to={'.'} /> },
];
